import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Plus, Loader2, Pencil, Power } from "lucide-react";
import { AddCodeDialog } from "./AddCodeDialog";
import { EditCodeDialog } from "./EditCodeDialog";

const API_URL = process.env.REACT_APP_API_URL || "";

interface Code {
  id: string;
  personalCode: string;
  name: string | null;
  note: string | null;
  isActive: boolean;
  createdAt: string;
}

interface Props {
  onChange?: () => void;
}

export function WhitelistTab({ onChange }: Props) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Code[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Code | null>(null);

  const fetchCodes = useCallback(async (q: string = "") => {
    setLoading(true);
    try {
      const res = await axios.get<{ success: boolean; data: Code[] }>(
        `${API_URL}/api/admin/whitelist?search=${encodeURIComponent(q)}&limit=100`,
        { withCredentials: true }
      );
      if (res.data.success) setItems(res.data.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes("");
  }, [fetchCodes]);

  const handleToggleActive = async (code: Code) => {
    if (code.isActive) {
      if (!window.confirm(t("admin.whitelist.confirmDelete"))) return;
      await axios.delete(`${API_URL}/api/admin/whitelist/${code.id}`, { withCredentials: true });
    } else {
      await axios.patch(
        `${API_URL}/api/admin/whitelist/${code.id}`,
        { isActive: true },
        { withCredentials: true }
      );
    }
    await fetchCodes(search);
    onChange?.();
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Input
          placeholder={t("admin.whitelist.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchCodes(search)}
          className="max-w-sm"
        />
        <Button variant="outline" onClick={() => fetchCodes(search)}>
          {t("common.search")}
        </Button>
        <div className="flex-1" />
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t("admin.whitelist.addCode")}
        </Button>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("admin.whitelist.colCode")}</TableHead>
              <TableHead>{t("admin.whitelist.colName")}</TableHead>
              <TableHead>{t("admin.whitelist.colNote")}</TableHead>
              <TableHead>{t("admin.whitelist.colStatus")}</TableHead>
              <TableHead>{t("admin.whitelist.colAdded")}</TableHead>
              <TableHead className="text-right">{t("admin.whitelist.colActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                  —
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono">{c.personalCode}</TableCell>
                  <TableCell>{c.name ?? "—"}</TableCell>
                  <TableCell className="max-w-[240px] truncate">{c.note ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={c.isActive ? "default" : "secondary"}>
                      {c.isActive
                        ? t("admin.whitelist.statusActive")
                        : t("admin.whitelist.statusInactive")}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(c.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleToggleActive(c)}>
                      <Power className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <AddCodeDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={() => {
          fetchCodes(search);
          onChange?.();
        }}
      />
      <EditCodeDialog
        code={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => {
          fetchCodes(search);
          onChange?.();
        }}
      />
    </div>
  );
}
